
// @ is an alias to /src
import Vue from "vue";
import ListItems from "./components/ListItems";
import { DatabaseSelector } from "@/components/DatabaseSelector";
import { logout as amplifyLogout } from "@/auth/client";
import { constants } from "@/utils/constants";
import { getFeatureFlags } from "@/api";

interface MenuItem {
  title: string;
  icon: string;
  link: string;
  featured?: boolean;
  inProgress?: boolean;
  tooltip?: string;
  searchTerms?: string[];
}

export default Vue.extend({
  name: "home",
  components: { ListItems, DatabaseSelector },
  props: {
    value: {
      type: Boolean,
      default: true
    },
    mobile: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      menuSearchQuery: "" as string,
      selectedMenuItem: null as MenuItem | null,

      // USER - User management
      userItems: [
        {
          title: "User",
          icon: "account_circle",
          link: "/tables/User"
        }
      ] as MenuItem[],

      // TALENT - Everything related to dental professionals
      talentItems: [
        {
          title: "Talent",
          icon: "dashboard",
          link: "/talent",
          featured: true
        },
        {
          title: "Duplicate Review",
          icon: "content_copy",
          link: "/duplicate-review"
        },
        {
          title: "Blocked hygienist",
          icon: "person_add_disabled",
          link: "/tables/BlockedHygienist"
        },
        {
          title: "Favourite hygienist",
          icon: "person_add",
          link: "/tables/FavouriteHygienist"
        }
      ] as MenuItem[],

      // DENTAL OFFICES - Everything related to dental office locations
      dentalOfficesItems: [
        {
          title: "Location",
          icon: "location_on",
          link: "/dental-offices"
        },
        {
          title: "Blocked dentist",
          icon: "domain_disabled",
          link: "/tables/BlockedDentist"
        },
        {
          title: "Favourite dentist",
          icon: "apartment",
          link: "/tables/FavouriteDentist"
        }
      ] as MenuItem[],

      // CONNECTIONS - Everything related to bookings, billing, connections between sides
      connectionsItems: [
        {
          title: "GeoViz Map",
          icon: "mdi-map-marker-radius",
          link: "/geo-viz",
          featured: true
        },
        {
          title: "Daily Shift List",
          icon: "mdi-format-list-checks",
          link: "/tables/DailyShiftList"
        },
        {
          title: "Jobs",
          icon: "work_outline",
          link: "/tables/Job"
        },
        {
          title: "Shifts",
          icon: "mdi-calendar-range",
          link: "/tables/Shift"
        },
        {
          title: "Invoices",
          icon: "assignment",
          link: "/invoices"
        },
        {
          title: "Stripe Express Accounts",
          icon: "mdi-bank-transfer",
          link: "/stripe-express-accounts",
          searchTerms: ["Stripe", "payouts", "Express", "restricted", "onboarding"]
        },
        {
          title: "Raw Data",
          icon: "mdi-database",
          link: "/raw-data",
          featured: true
        },
        {
          title: "SNC Billing Board",
          icon: "mdi-file-account",
          link: "/tables/SNCBillingBoard"
        },
        {
          title: "SNC by Hyg/Da",
          icon: "remove_done",
          link: "/SNCbyHygDa"
        },
        {
          title: "Chat History",
          icon: "mdi-message-text",
          link: "/chat-history"
        }
      ] as MenuItem[],

      jobBoardItems: [
        {
          title: "Job Board Tables",
          icon: "mdi-briefcase-search",
          link: "/job-board",
          searchTerms: [
            "Job Board Applications",
            "Job Board Posts",
            "Featured Listings",
            "Boosted Posts"
          ]
        }
      ] as MenuItem[],

      // OTHER ROUTES - General admin functions that don't fit the three main categories
      otherRoutes: [
        {
          title: "Office acquisition",
          icon: "mdi-chart-timeline-variant",
          link: "/office-acquisition",
          searchTerms: ["Revenue", "Marketing", "Campaigns", "Attribution"]
        },
        {
          title: "Nora Support",
          icon: "mdi-chat-question-outline",
          link: "/support-assistant",
          featured: true
        },
        {
          title: "Photo Announce",
          icon: "mdi-image-edit",
          link: "/photo-announce"
        },
        {
          title: "Manual Shift Booking",
          icon: "mdi-calendar-plus",
          link: "/manual-shift-booking"
        },
        {
          title: "History",
          icon: "history",
          link: "/history"
        },
        {
          title: "Review History",
          icon: "history",
          link: "/review-history"
        },
        {
          title: "Email Tracker",
          icon: "mdi-email-check",
          link: "/email-tracker"
        },
        {
          title: "Bucket Email",
          icon: "mdi-email-search-outline",
          link: "/bucket-email",
          searchTerms: ["Support inbox", "shared Gmail", "inbound email"]
        },
        {
          title: "Spam List",
          icon: "mdi-email-off",
          link: "/spam-list"
        },
        {
          // T2-6236: Reliability List repurposed as the live Available Now list
          title: "Available Now",
          icon: "mdi-flash",
          link: "/available-now"
        }
      ] as MenuItem[],
      devToolsItems: [] as MenuItem[],
      userAllowed: null as any
    };
  },
  computed: {
    drawerValue: {
      get(): boolean {
        return (this as any).value;
      },
      set(value: boolean) {
        (this as any).$emit("input", value);
      }
    },
    isHomeRoute(): boolean {
      return this.$route.path === "/" || this.$route.path === "";
    },
    envName(): string {
      const hostname = window.location.hostname.toLowerCase();
      const configuredEnvironment = process.env.VUE_APP_DASHBOARD_ENVIRONMENT;
      
      // Check hostname first to determine actual environment
      if (hostname === "dashboard.tempstars.com") {
        return "Production";
      }

      if (
        hostname === "dashboard.dev.tempstars.com" ||
        hostname.includes(".dev.")
      ) {
        return "Development";
      }
      
      // Improved Staging Detection - Check for beta/staging in hostname BEFORE checking NODE_ENV
      if (
        hostname === "dashboard.beta.tempstars.com" ||
        hostname.includes("beta") ||
        hostname.includes("staging")
      ) {
        return "Staging";
      }
      
      if (hostname === "localhost" || hostname === "127.0.0.1") {
        return "Development";
      }

      if (
        configuredEnvironment === "Production" ||
        configuredEnvironment === "Staging" ||
        configuredEnvironment === "Development"
      ) {
        return configuredEnvironment;
      }

      // Fallback checks
      if (process.env.NODE_ENV === "staging") {
        return "Staging";
      }
      
      if (process.env.NODE_ENV === "development") {
        return "Development";
      }
      
      return "Production";
    },
    envColor(): string {
      const name = this.envName;
      if (name === "Production") {
        return "#13CA82";
      }
      if (name === "Staging") {
        return "#FF9800";
      }
      if (name === "Development") {
        return "#2196F3";
      }
      return "#FF9800"; // Default orange for "Check URL" cases
    },
    allMenuItems(): MenuItem[] {
      const items: MenuItem[] = [];

      // Add Home
      items.push({
        title: "Home",
        icon: "mdi-home",
        link: "/"
      });

      // Add User items
      this.userItems.forEach(item => {
        items.push(item);
      });

      // Add Talent items
      this.talentItems.forEach(item => {
        items.push(item);
      });

      // Add Dental Offices items
      this.dentalOfficesItems.forEach(item => {
        items.push(item);
      });

      // Add Connections items
      this.connectionsItems.forEach(item => {
        items.push(item);
      });

      this.jobBoardItems.forEach(item => {
        items.push(item);
      });

      // Add Admin Tools items
      this.otherRoutes.forEach(item => {
        items.push(item);
      });

      // Add Dev Tools items
      this.devToolsItems.forEach(item => {
        items.push(item);
      });

      // Filter if search query exists
      if (this.menuSearchQuery) {
        const query = this.menuSearchQuery.toLowerCase();
        return items.filter(item => {
          const searchable = [item.title, ...(item.searchTerms || [])];
          return searchable.some(term => term.toLowerCase().includes(query));
        });
      }
      
      return items;
    }
  },
  watch: {
    $route() {
      if ((this as any).mobile) {
        (this as any).drawerValue = false;
      }
    },
    mobile(isMobile: boolean) {
      (this as any).drawerValue = !isMobile;
    }
  },
  created() {
    try {
      const user = localStorage.getItem("user");
      if (user) {
        try {
          this.userAllowed = JSON.parse(user);
        } catch {
          // Fallback for legacy string storage or invalid JSON
          this.userAllowed = { email: user, role: 0 };
        }
      }
    } catch (error) {
      this.userAllowed = null;
    }

    // Check permissions based on role or legacy username check
    const roleVal = this.userAllowed?.role;
    const userRole = roleVal ? parseInt(String(roleVal), 10) : constants.ADMIN_ROLES.REGULAR;
    const isDeveloper = userRole === constants.ADMIN_ROLES.DEVELOPER;
    
    // Add System Crons only if NOT in production AND user is dev (developer role)
    if (this.envName !== "Production" && isDeveloper) {
      this.devToolsItems.push({
        title: "System Crons",
        icon: "mdi-clock-outline",
        link: "/system/cron"
      });
    }

    // Mobile Build Trigger - Developer role only. Allowed in production
    // because production TestFlight / Play Internal uploads are the whole
    // reason this exists.
    if (isDeveloper) {
      this.devToolsItems.push({
        title: "Mobile Build Trigger",
        icon: "mdi-cellphone-arrow-down",
        link: "/system/build-trigger"
      });
    }

    // Add Partner Referral Codes for all admin users
    this.otherRoutes.push({
      title: "Partner Referral Codes",
      icon: "mdi-ticket-percent",
      link: "/system/partner-codes"
    });

    // T2-6420: Job Board partner (Recruit North America) link, editable by all
    // admin users.
    this.otherRoutes.push({
      title: "Job Board Partner Link",
      icon: "mdi-link-variant",
      link: "/system/job-board-partner-link"
    });

    // Courses - TalentLMS catalog sync, available to all admin users
    this.otherRoutes.push({
      title: "Courses",
      icon: "mdi-school-outline",
      link: "/courses"
    });

    // Partner Affiliation - Developer role only
    if (isDeveloper) {
      this.otherRoutes.push({
        title: "Partner Affiliation",
        icon: "mdi-domain",
        link: "/admin/partner-affiliation"
      });
    }

    // Bulk Placement Fee Upload + Regional Free Periods — role-1, Admin Tools (T2-6927)
    // Same pattern as Partner Affiliation: isDeveloper gate, listed under otherRoutes.
    if (isDeveloper) {
      // T2-6984: SNC Merch Recipients — role-1, under Connections near SNC boards
      const alreadyAdded = this.connectionsItems.some(
        (item: MenuItem) => item.link === "/snc-merch-recipients"
      );
      if (!alreadyAdded) {
        const sncByHygIdx = this.connectionsItems.findIndex(
          (item: MenuItem) => item.link === "/SNCbyHygDa"
        );
        const merchEntry: MenuItem = {
          title: "SNC Merch Recipients",
          icon: "mdi-gift-outline",
          link: "/snc-merch-recipients"
        };
        if (sncByHygIdx >= 0) {
          this.connectionsItems.splice(sncByHygIdx + 1, 0, merchEntry);
        } else {
          this.connectionsItems.push(merchEntry);
        }
      }

      this.otherRoutes.push({
        title: "Bulk Placement Fee Upload",
        icon: "mdi-upload-multiple",
        link: "/system/bulk-placement-fees"
      });
      this.otherRoutes.push({
        title: "Regional Free Periods",
        icon: "mdi-calendar-star",
        link: "/system/free-periods"
      });
      // Office Promotions stays in Dev Tools (T2-6869; not in T2-6927 scope)
      this.devToolsItems.push({
        title: "Office Promotions",
        icon: "mdi-map-marker-star",
        link: "/system/office-promotions"
      });
      // T2-6151: Message Users (in-app Bell) — Developer role only
      this.devToolsItems.push({
        title: "Message Users",
        icon: "mdi-bell-badge-outline",
        link: "/system/message-users"
      });
    }

    // T2-6905 / T2-6910: Feature Flags — allowlist-driven (API gate), not role-1 hardcoded.
    getFeatureFlags()
      .then(() => {
        this.otherRoutes.push({
          title: "Feature Flags",
          icon: "mdi-toggle-switch",
          link: "/system/feature-flags"
        });
      })
      .catch(() => {
        // 403 / network: leave nav item hidden
      });
  },
  methods: {
    filterMenuItems(): void {
      // The computed properties handle the filtering automatically
      // This method can be used for additional logic if needed
    },
    handleMenuSelect(item: MenuItem | null): void {
      if (item && item.link) {
        this.$router.push(item.link);
        this.menuSearchQuery = "";
        this.selectedMenuItem = null;
        if ((this as any).mobile) {
          (this as any).drawerValue = false;
        }
      }
    },
    async logout(): Promise<void> {
      try {
        await amplifyLogout();
      } catch (error) {
        // Continue with local logout even if Amplify fails
        console.error("Logout error:", error);
      }

      // Clear all local storage
      localStorage.clear();

      // Clear session storage as well
      sessionStorage.clear();

      // T2-6344: tell App.vue to drop its reactive `auth` flag so the Drawer
      // and top Bar hide immediately (the Amplify `signedOut` Hub event does
      // not fire for password/direct-login admins).
      this.$root.$emit("appSignOut");

      this.$router.push("/login");
    }
  }
});
